import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import styled from "styled-components";

import { ArrowLeftColor } from "@components";
import { PATH_BMIFORM } from "@configs";
import { selectAuth, selectMerchant } from "@redux";
import { RenderMeta } from "@utils";
import { useEffect, useState } from "react";
import { bmiApi } from "src/API/bmi";
import { useFullScreen } from "src/Helpers/useFullscreen";

// Interface matching API response
interface BmiHistoryItem {
    _id?: string;
    gender: string;
    age: number;
    weight: number;
    height: number;
    bmi: number;
    bmiStatus: string;
    calories: number;
    protein: number;
    carbohydrates: number;
    fat: number;
    sugar: number;
    saturatedFat: number;
    goal: string;
    createdAt: string;
    updatedAt?: string;
}

// Helper function to get additional status info
const getAdditionalStatus = (bmi: number): string => {
    if (bmi >= 18.5 && bmi < 19) return "(Borderline Underweight)";
    if (bmi >= 24 && bmi < 25) return "(Borderline Overweight)";
    return "";
};

// Helper function to format date
const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day}/${month}/${year}, ${hours}:${minutes}`;
};

export const ComponentBmiHistory = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { userInfo } = useSelector(selectAuth);
    const { themes } = useSelector(selectMerchant);
    const merchant = useSelector(selectMerchant);
    const fullHeight = useFullScreen();

    const [historyList, setHistoryList] = useState<BmiHistoryItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchBmiHistory();
    }, []);

    const fetchBmiHistory = async () => {
        setIsLoading(true);
        try {
            const response = await bmiApi.getMyBmiRecords({ page: 1, limit: 50 });
            // Assuming API returns { data: { docs: [...] } } or { data: [...] }
            const records = response?.data?.docs || response?.data || [];
            setHistoryList(records);
        } catch (error) {
            console.error("Failed to fetch BMI history:", error);
            setHistoryList([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleItemClick = (item: BmiHistoryItem) => {
        // Navigate to result page with pre-calculated data from API
        history.push({
            pathname: "/bmi-result",
            state: {
                gender: item.gender,
                age: item.age,
                weight: item.weight,
                height: item.height,
                bmi: item.bmi,
                bmiStatus: item.bmiStatus,
                calories: item.calories,
                protein: item.protein,
                carbohydrates: item.carbohydrates,
                fat: item.fat,
                sugar: item.sugar,
                saturatedFat: item.saturatedFat,
                goalText: item.goal,
            },
        });
    };

    return (
        <>
            <RenderMeta title="BMI History" />
            <StyledBmiHistory style={{ height: fullHeight }}>
                <header>
                    <div className="back-arrow">
                        <ArrowLeftColor onClick={() => history.push(PATH_BMIFORM)} color="#000" />
                    </div>
                    <h1 className="page-title">BMI Calculator History</h1>
                </header>

                <div className="history-list">
                    {isLoading ? (
                        <div className="empty-state">
                            <p>Loading...</p>
                        </div>
                    ) : historyList.length === 0 ? (
                        <div className="empty-state">
                            <p>No BMI history</p>
                        </div>
                    ) : (
                        historyList.map((item, index) => {
                            const additionalStatus = getAdditionalStatus(item.bmi);

                            return (
                                <div
                                    key={item._id || index}
                                    className="history-item"
                                    onClick={() => handleItemClick(item)}
                                >
                                    <div className="item-icon">
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M8.9043 0.833008C9.50613 0.832981 10.0145 0.832902 10.417 0.879883C10.8369 0.928922 11.2469 1.03947 11.5801 1.33984C11.7302 1.47521 11.8578 1.63288 11.958 1.80762C12.155 2.15119 12.1866 2.51655 12.1553 2.87891C12.8061 2.95392 13.3466 3.12425 13.7686 3.54688C14.1693 3.94855 14.3412 4.45421 14.4219 5.05469C14.4995 5.63354 14.5 6.36983 14.5 7.2832V10.6953C14.5 11.6086 14.4995 12.345 14.4219 12.9238C14.3412 13.5246 14.1696 14.0308 13.7686 14.4326C13.3674 14.8345 12.8618 15.0071 12.2617 15.0879C11.6838 15.1657 10.9486 15.166 10.0371 15.166H5.96289C5.05143 15.166 4.31622 15.1657 3.73828 15.0879C3.13847 15.0071 2.63349 14.8341 2.23242 14.4326C1.83135 14.0308 1.65879 13.5246 1.57812 12.9238C1.50044 12.345 1.49999 11.6087 1.5 10.6953V7.2832C1.49999 6.36981 1.50044 5.63353 1.57812 5.05469C1.65879 4.45398 1.83136 3.94862 2.23242 3.54688C2.65411 3.12448 3.19336 2.95398 3.84375 2.87891C3.81249 2.51658 3.84503 2.15116 4.04199 1.80762C4.14217 1.63288 4.2698 1.47521 4.41992 1.33984C4.75308 1.03947 5.1631 0.928921 5.58301 0.879883C5.98554 0.832902 6.49384 0.832981 7.0957 0.833008H8.9043ZM6.66699 11.333C6.29881 11.333 6 11.6319 6 12C6.00001 12.3682 6.29881 12.667 6.66699 12.667H9.33301C9.7012 12.667 9.99999 12.3682 10 12C10 11.6319 9.70121 11.333 9.33301 11.333H6.66699ZM7.13477 2.09961C6.48358 2.09961 6.05172 2.10152 5.7334 2.13867C5.42431 2.17479 5.32869 2.23528 5.28418 2.27539C5.23324 2.32132 5.19033 2.37394 5.15723 2.43164C5.13103 2.47734 5.09379 2.57375 5.13184 2.86719C5.17135 3.17159 5.27441 3.57412 5.43262 4.18555C5.55858 4.67238 5.64176 4.99217 5.73047 5.23145C5.81447 5.45799 5.88407 5.55726 5.95215 5.62207C6.00617 5.67346 6.06652 5.71926 6.13184 5.75781C6.21607 5.80751 6.33694 5.84982 6.58887 5.87402C6.75831 5.89028 6.96074 5.89419 7.2207 5.89648L7.70117 4.45605C7.81756 4.10689 8.19478 3.91797 8.54395 4.03418C8.89321 4.15061 9.08229 4.52864 8.96582 4.87793L8.625 5.89746C8.96149 5.89618 9.20956 5.89336 9.41113 5.87402C9.66307 5.84982 9.78394 5.80751 9.86816 5.75781C9.93346 5.71926 9.99381 5.67346 10.0479 5.62207C10.1159 5.55726 10.1855 5.45799 10.2695 5.23145C10.3582 4.99217 10.4414 4.67237 10.5674 4.18555C10.7256 3.57412 10.8287 3.17159 10.8682 2.86719C10.9062 2.57376 10.869 2.47734 10.8428 2.43164C10.8097 2.37394 10.7668 2.32132 10.7158 2.27539C10.6714 2.23528 10.5757 2.17479 10.2666 2.13867C9.94827 2.10152 9.51643 2.09961 8.86523 2.09961H7.13477Z"
                                                fill="#76C0D9"
                                            />
                                        </svg>
                                    </div>
                                    <div className="item-content">
                                        <div className="bmi-score">
                                            {item.bmi.toFixed(1)} {item.bmiStatus.toUpperCase()}
                                            {additionalStatus && (
                                                <span className="additional-status">
                                                    {" "}
                                                    {additionalStatus}
                                                </span>
                                            )}
                                        </div>
                                        <div className="date-row">
                                            <svg
                                                width="10"
                                                height="10"
                                                viewBox="0 0 10 10"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M8.71094 0.78125H7.8125V0.3125C7.8125 0.139922 7.6726 0 7.5 0C7.3274 0 7.1875 0.139922 7.1875 0.3125V0.78125H2.8125V0.3125C2.8125 0.139922 2.6726 0 2.5 0C2.3274 0 2.1875 0.139922 2.1875 0.3125V0.78125H1.28906C0.578262 0.78125 0 1.35951 0 2.07031V8.71094C0 9.42174 0.578262 10 1.28906 10H8.71094C9.42174 10 10 9.42174 10 8.71094V2.07031C10 1.35951 9.42174 0.78125 8.71094 0.78125ZM9.375 8.71094C9.375 9.07769 9.07769 9.375 8.71094 9.375H1.28906C0.922305 9.375 0.625 9.07769 0.625 8.71094V3.53516C0.625 3.48123 0.66873 3.4375 0.722656 3.4375H9.27734C9.33127 3.4375 9.375 3.48123 9.375 3.53516V8.71094Z"
                                                    fill="#898989"
                                                />
                                            </svg>

                                            <span>{formatDate(item.createdAt)}</span>
                                        </div>
                                        <div className="details-row">
                                            <span>Weight : {item.weight} KG</span>
                                            <span className="separator">|</span>
                                            <span>Height : {item.height} CM</span>
                                            <span className="separator">|</span>
                                            <span>Age : {item.age}</span>
                                            <span className="separator">|</span>
                                            <span>Gender : {item.gender}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </StyledBmiHistory>
        </>
    );
};

const StyledBmiHistory = styled.div`
    width: 100%;
    margin: 0 auto;
    height: 100vh;
    overflow-y: auto;
    background-color: #fff;
    position: relative;

    header {
        display: flex;
        align-items: center;
        padding: 16px;
        padding-top: 40px;
        position: relative;

        .back-arrow {
            position: absolute;
            left: 16px;
            top: 40px;
            cursor: pointer;
        }

        .page-title {
            flex: 1;
            font-size: 20px;
            font-weight: 600;
            color: #000;
            margin: 0;
            padding-left: 40px;
        }
    }

    .history-list {
        padding: 20px 16px;

        .empty-state {
            text-align: center;
            padding: 60px 20px;
            color: #999;

            p {
                margin: 8px 0;
                font-size: 14px;
            }
        }

        .history-item {
            display: flex;
            align-items: flex-start;
            padding: 16px 0;
            border-bottom: 1px solid #f0f0f0;
            cursor: pointer;
            transition: background-color 0.2s;

            &:hover {
                background-color: #f9f9f9;
            }

            &:last-child {
                border-bottom: none;
            }

            .item-icon {
                width: 40px;
                height: 40px;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-right: 12px;
                flex-shrink: 0;
                background-color: #e8f4f8;
                border-radius: 50%;
            }

            .item-content {
                flex: 1;

                .bmi-score {
                    font-size: 16px;
                    font-weight: 600;
                    color: #000;
                    margin-bottom: 4px;

                    .additional-status {
                        font-weight: 400;
                        color: #666;
                    }
                }

                .date-row {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    color: #999;
                    font-size: 13px;
                    margin-bottom: 8px;
                }

                .details-row {
                    padding-top: 8px;
                    font-size: 11px;
                    color: #666;
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 4px;

                    .separator {
                        color: #ccc;
                    }
                }
            }
        }
    }
`;
